import { useState, useMemo } from 'react'
import { useTheme } from '../context/ThemeContext'

/**
 * Daily production "spreadsheet" grid for Well Analysis.
 * Columns: Date | Oil | Gas | Water | Water Inj | Inj Press | (Tubing) | (Casing) | Runtime | Downtime | Notes
 * - Tubing/Casing pressure columns only appear when the selection has any values.
 * - Range selector (30/60/90/All days), click-to-sort headers, newest-first default.
 *
 * @param {Array} rows - dailyGrid from /api/well-analysis (ascending by date)
 * @param {boolean} dense - tighter layout for side-by-side compare
 */
const RANGES = [
    { key: '30', label: '30D', days: 30 },
    { key: '60', label: '60D', days: 60 },
    { key: '90', label: '90D', days: 90 },
    { key: 'all', label: 'All', days: 0 }
]

function DailyDataTable({ rows = [], dense = false }) {
    const { theme } = useTheme()
    const isDark = theme === 'dark'

    const [range, setRange] = useState('30')
    const [sortCol, setSortCol] = useState('date')
    const [sortAsc, setSortAsc] = useState(false) // newest first

    // Which optional pressure columns have any data in the full set?
    const hasTubing = useMemo(() => rows.some(r => r.tubing_pressure_psi != null), [rows])
    const hasCasing = useMemo(() => rows.some(r => r.casing_pressure_psi != null), [rows])

    const cols = useMemo(() => {
        const c = [
            { key: 'date', label: 'Date', align: 'left', type: 'date' },
            { key: 'oil_bbl', label: 'Oil (BBL)', align: 'right', type: 'int' },
            { key: 'gas_mcf', label: 'Gas (MCF)', align: 'right', type: 'int' },
            { key: 'water_bbl', label: 'Water (BBL)', align: 'right', type: 'int' },
            { key: 'inj_water_bbl', label: 'Water Inj (BBL)', align: 'right', type: 'int' },
            { key: 'inj_pressure_psi', label: 'Inj Press (psi)', align: 'right', type: 'int' }
        ]
        if (hasTubing) c.push({ key: 'tubing_pressure_psi', label: 'Tbg (psi)', align: 'right', type: 'int' })
        if (hasCasing) c.push({ key: 'casing_pressure_psi', label: 'Csg (psi)', align: 'right', type: 'int' })
        c.push(
            { key: 'runtime_hours', label: 'Runtime (hr)', align: 'right', type: 'dec' },
            { key: 'downtime_hours', label: 'Downtime (hr)', align: 'right', type: 'dec' },
            { key: 'notes', label: 'Downtime Notes', align: 'left', type: 'text' }
        )
        return c
    }, [hasTubing, hasCasing])

    const displayRows = useMemo(() => {
        let data = rows
        if (range !== 'all') {
            const r = RANGES.find(x => x.key === range)
            const cutoff = new Date()
            cutoff.setDate(cutoff.getDate() - r.days)
            const cutoffStr = cutoff.toISOString().slice(0, 10)
            data = data.filter(d => d.date >= cutoffStr)
        }
        const sorted = [...data].sort((a, b) => {
            const av = a[sortCol], bv = b[sortCol]
            if (av == null && bv == null) return 0
            if (av == null) return 1
            if (bv == null) return -1
            if (typeof av === 'string') return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av)
            return sortAsc ? av - bv : bv - av
        })
        return sorted
    }, [rows, range, sortCol, sortAsc])

    const handleSort = (key) => {
        if (sortCol === key) setSortAsc(!sortAsc)
        else { setSortCol(key); setSortAsc(key === 'date' ? false : false) }
    }
    const arrow = (key) => sortCol === key ? (sortAsc ? ' ▲' : ' ▼') : ''

    const fmt = (val, type) => {
        if (val == null || val === '') return type === 'text' ? '' : '--'
        if (type === 'int') return Math.round(val).toLocaleString()
        if (type === 'dec') return Number(val).toFixed(1)
        if (type === 'date') return new Date(val + 'T00:00:00').toLocaleDateString('en-US', { year: '2-digit', month: 'numeric', day: 'numeric' })
        return val
    }

    if (!rows.length) {
        return (
            <div className={`flex items-center justify-center h-40 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                No production data available
            </div>
        )
    }

    return (
        <div>
            {/* Range selector */}
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-1.5">
                    {RANGES.map(r => (
                        <button key={r.key} onClick={() => setRange(r.key)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                range === r.key
                                    ? 'bg-blue-600 text-white'
                                    : isDark ? 'bg-slate-700 text-gray-300 hover:bg-slate-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}>
                            {r.label}
                        </button>
                    ))}
                </div>
                <span className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {displayRows.length} days — click headers to sort
                </span>
            </div>

            <div className="overflow-auto" style={{ maxHeight: dense ? '24rem' : '32rem' }}>
                <table className="data-table">
                    <thead className="sticky top-0 z-10">
                        <tr>
                            {cols.map(c => (
                                <th key={c.key}
                                    onClick={() => handleSort(c.key)}
                                    className={`${c.align === 'right' ? 'text-right' : ''} cursor-pointer select-none hover:text-blue-500 transition-colors whitespace-nowrap ${isDark ? 'bg-slate-900' : 'bg-white'}`}
                                >
                                    {c.label}{arrow(c.key)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {displayRows.map((row, i) => (
                            <tr key={row.date || i}>
                                {cols.map(c => {
                                    if (c.key === 'notes') {
                                        return (
                                            <td key={c.key} className={`text-xs max-w-[16rem] truncate ${isDark ? 'text-gray-400' : 'text-gray-600'}`} title={row.notes || ''}>
                                                {row.notes || ''}
                                            </td>
                                        )
                                    }
                                    const down = c.key === 'downtime_hours' && row.downtime_hours > 0
                                    return (
                                        <td key={c.key}
                                            className={`${c.align === 'right' ? 'text-right font-mono' : ''} ${c.key === 'date' ? 'whitespace-nowrap font-medium' : ''} ${
                                                down ? (isDark ? 'text-red-400' : 'text-red-600') : ''
                                            }`}>
                                            {fmt(row[c.key], c.type)}
                                        </td>
                                    )
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default DailyDataTable
